import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { Newspaper, Bell, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { usePublishedNews } from '@/hooks/useNews';
import { format } from 'date-fns';
import { SEO, getBreadcrumbSchema } from '@/hooks/useSEO';

export default function NewsPage() {
  const { t } = useTranslation();
  const { lang = 'sv' } = useParams();
  const { data: articles, isLoading } = usePublishedNews();

  const getExcerpt = (article: { excerpt: string | null; content: string }) => {
    if (article.excerpt) return article.excerpt;
    // Strip HTML tags and take first 150 chars
    const text = article.content.replace(/<[^>]+>/g, '');
    return text.length > 150 ? text.slice(0, 150) + '…' : text;
  };

  return (
    <div className="container py-12 lg:py-16">
      <SEO
        title={t('information.news.title')}
        description={t('meta.news')}
        path="/information/nyheter"
        jsonLd={getBreadcrumbSchema([
          { name: t('common.home'), url: `https://postnummerservice.se/${lang}` },
          { name: t('common.information'), url: `https://postnummerservice.se/${lang}/information/nyheter` },
          { name: t('information.news.title'), url: `https://postnummerservice.se/${lang}/information/nyheter` },
        ])}
      />
      <div className="max-w-4xl mx-auto space-y-12">
        {/* Hero Section */}
        <section>
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
              <Newspaper className="w-6 h-6 text-secondary" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                {t('information.news.title')}
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {t('information.news.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Articles */}
        {!isLoading && articles && articles.length > 0 && (
          <section className="space-y-4">
            {articles.map((article) => (
              <Link
                key={article.id}
                to={`/${lang}/information/nyheter/${article.id}`}
                className="block group"
              >
                <Card className="transition-colors hover:border-secondary/30">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-1.5 min-w-0">
                        <h2 className="text-lg font-semibold group-hover:text-secondary transition-colors">
                          {article.title}
                        </h2>
                        <time className="text-xs text-muted-foreground">
                          {article.published_at
                            ? format(new Date(article.published_at), 'yyyy-MM-dd')
                            : format(new Date(article.created_at), 'yyyy-MM-dd')}
                        </time>
                        <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                          {getExcerpt(article)}
                        </p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-1 group-hover:text-secondary transition-colors" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </section>
        )}

        {/* Empty state */}
        {!isLoading && (!articles || articles.length === 0) && (
          <section>
            <Card>
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
                  <Bell className="w-8 h-8 text-muted-foreground" />
                </div>
                <h2 className="text-xl font-semibold mb-2">{t('information.news.comingSoon')}</h2>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {t('information.news.comingSoonDescription')}
                </p>
              </CardContent>
            </Card>
          </section>
        )}
      </div>
    </div>
  );
}
